import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from '../assets/inspectionTypes.module.css';
import { inspectionTypesService } from '../services/inspectionTypesService';

const InspectionTypes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [useMockData, setUseMockData] = useState(false);
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [tableSearch, setTableSearch] = useState('');
  const [filteredInspectionTypes, setFilteredInspectionTypes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [columnVisibility, setColumnVisibility] = useState({
    name: true,
    color: true,
    actions: true
  });
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const searchDebounceTimerRef = useRef(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const searchInputRef = useRef(null);
  const isFilteringRef = useRef(false);

  // Mock data generation
  const generateMockInspectionTypes = () => {
    return [
      {
        id: 1,
        name: 'Compliance Check',
        color: '#0d43c1'
      },
      {
        id: 2,
        name: 'Final Inspection',
        color: '#dc3545'
      },
      {
        id: 3,
        name: 'Health & Safety Inspection',
        color: '#28a745'
      },
      {
        id: 4,
        name: 'Initial Inspection',
        color: '#ffc107'
      },
      {
        id: 5,
        name: 'Maintenance Review',
        color: '#17a2b8'
      },
      {
        id: 6,
        name: 'Monthly Inspection',
        color: '#6f42c1'
      },
      {
        id: 7,
        name: 'Property Condition Report',
        color: '#fd7e14'
      },
      {
        id: 8,
        name: 'Quality Assurance',
        color: '#e83e8c'
      }
    ];
  };

  useEffect(() => {
    loadInspectionTypes();
  }, [useMockData]);

  // Refresh data when component comes into focus (e.g., returning from create/edit page)
  useEffect(() => {
    const handleFocus = () => {
      loadInspectionTypes();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Filter function - prevents infinite loops
  const performFilter = useCallback((pageToUse = null) => {
    // Prevent concurrent filtering
    if (isFilteringRef.current) {
      return;
    }
    
    isFilteringRef.current = true;
    setIsFiltering(true);
    
    try {
      const page = pageToUse !== null ? pageToUse : currentPage;
      
      // Filter inspection types based on search
      let filtered = [];
      if (useMockData) {
        filtered = inspectionTypes.filter(type =>
          type.name.toLowerCase().includes(tableSearch.toLowerCase()) ||
          (type.color && type.color.toLowerCase().includes(tableSearch.toLowerCase()))
        );
      } else {
        // For real API, we could implement server-side search
        // For now, we'll do client-side filtering
        filtered = inspectionTypes.filter(type =>
          type.name.toLowerCase().includes(tableSearch.toLowerCase()) ||
          (type.color && type.color.toLowerCase().includes(tableSearch.toLowerCase()))
        );
      }
      
      // Calculate pagination
      const total = filtered.length;
      const pages = Math.max(1, Math.ceil(total / entriesPerPage)); // Ensure at least 1 page
      setTotalPages(pages);
      setTotalRecords(total);
      
      // Ensure current page is valid - prevent infinite loop
      const validPage = Math.min(Math.max(1, page), pages);
      
      // Only update current page if it's actually invalid and we're not forcing a page
      if (validPage !== page && pages > 0 && pageToUse === null) {
        // Update page state, but this won't trigger another filter since we check isFilteringRef
        setCurrentPage(validPage);
      }
      
      // Get current page data
      const startIndex = (validPage - 1) * entriesPerPage;
      const endIndex = startIndex + entriesPerPage;
      setFilteredInspectionTypes(filtered.slice(startIndex, endIndex));
    } catch (error) {
      console.error('Error filtering inspection types:', error);
      toast.error('Error filtering inspection types');
      setFilteredInspectionTypes([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setIsFiltering(false);
      isFilteringRef.current = false;
    }
  }, [inspectionTypes, tableSearch, entriesPerPage, useMockData, currentPage]);

  // Debounced effect for search
  useEffect(() => {
    // Clear existing timer
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    // Reset to first page when search changes
    if (tableSearch !== '') {
      setCurrentPage(1);
    }

    // Set new timer for debounced search
    searchDebounceTimerRef.current = setTimeout(() => {
      performFilter(1); // Always use page 1 when search changes
    }, 300); // 300ms debounce delay

    // Cleanup timer on unmount or when dependencies change
    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [tableSearch, inspectionTypes, entriesPerPage, useMockData, performFilter]);

  // Separate effect for pagination changes (without debounce)
  useEffect(() => {
    if (!isFilteringRef.current && inspectionTypes.length > 0) {
      performFilter();
    }
  }, [currentPage, performFilter]);

  const loadInspectionTypes = async () => {
    setLoading(true);
    try {
      if (useMockData) {
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 500));
        const mockData = generateMockInspectionTypes();
        setInspectionTypes(mockData);
        setFilteredInspectionTypes(mockData.slice(0, entriesPerPage));
        setTotalRecords(mockData.length);
      } else {
        const response = await inspectionTypesService.getInspectionTypes();
        
        // Handle DataTables response format
        if (response.data && response.data.data) {
          // DataTables format
          const types = response.data.data.map(item => ({
            id: item.id,
            name: item.name,
            color: item.color
          }));
          setInspectionTypes(types);
          setFilteredInspectionTypes(types.slice(0, entriesPerPage));
          setTotalRecords(response.data.recordsTotal || types.length);
        } else if (Array.isArray(response.data)) {
          // Direct array format
          setInspectionTypes(response.data);
          setFilteredInspectionTypes(response.data.slice(0, entriesPerPage));
          setTotalRecords(response.data.length);
        } else {
          // Fallback
          setInspectionTypes([]);
          setFilteredInspectionTypes([]);
          setTotalRecords(0);
        }
      }
    } catch (error) {
      console.error('Error loading inspection types:', error);
      toast.error('Failed to load inspection types');
      // Fallback to empty array on error
      setInspectionTypes([]);
      setFilteredInspectionTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddInspectionType = () => {
    navigate('/inspection-types/create');
  };

  const handleEditInspectionType = (id) => {
    navigate(`/inspection-types/${id}/edit`);
  };

  const handleDeleteInspectionType = async (id) => {
    if (window.confirm('Are you sure you want to delete this inspection type?')) {
      try {
        if (useMockData) {
          // Simulate delete in mock data
          const updatedTypes = inspectionTypes.filter(type => type.id !== id);
          setInspectionTypes(updatedTypes);
          setFilteredInspectionTypes(updatedTypes.slice(0, entriesPerPage));
          toast.success('Inspection type deleted successfully');
        } else {
          await inspectionTypesService.deleteInspectionType(id);
          // Reload the list after successful deletion
          await loadInspectionTypes();
          toast.success('Inspection type deleted successfully');
        }
      } catch (error) {
        console.error('Error deleting inspection type:', error);
        toast.error('Failed to delete inspection type');
      }
    }
  };

  const toggleMockData = () => {
    setUseMockData(!useMockData);
  };

  const handleExportCSV = () => {
    try {
      const csvContent = [
        ['Name', 'Color'],
        ...filteredInspectionTypes.map(type => [
          `"${type.name}"`, 
          type.color ? `"${type.color}"` : ''
        ])
      ].map(row => row.join(',')).join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inspection-types-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  const handleExportPDF = async () => {
    try {
      // Dynamic import for jsPDF
      let jsPDF;
      try {
        jsPDF = (await import('jspdf')).default;
      } catch (importError) {
        console.error('Failed to import jsPDF dynamically:', importError);
        // Fallback to require
        jsPDF = require('jspdf').default;
      }

      // Create new PDF document
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Inspection Types', 20, 20);
      
      // Add date
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
      
      // Add total records
      doc.text(`Total Records: ${inspectionTypes.length}`, 20, 40);
      
      // Set up table parameters
      const startY = 50;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      const tableWidth = doc.internal.pageSize.width - (2 * margin);
      const colWidths = [20, 120, 50]; // #, Name, Color
      const rowHeight = 12;
      const headerHeight = 15;
      
      // Draw table headers
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, startY, tableWidth, headerHeight, 'F');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('#', margin + 5, startY + 10);
      doc.text('Name', margin + colWidths[0] + 5, startY + 10);
      doc.text('Color', margin + colWidths[0] + colWidths[1] + 5, startY + 10);
      
      // Draw table data
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      let currentY = startY + headerHeight;
      let pageNumber = 1;
      
      inspectionTypes.forEach((type, index) => {
        // Check if we need a new page
        if (currentY + rowHeight > pageHeight - 30) {
          doc.addPage();
          pageNumber++;
          currentY = 20;
          
          // Add page number
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.text(`Page ${pageNumber}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
        }
        
        // Draw row background (alternating colors)
        if (index % 2 === 0) {
          doc.setFillColor(248, 249, 250);
          doc.rect(margin, currentY, tableWidth, rowHeight, 'F');
        }
        
        // Draw row content
        doc.text(String(index + 1), margin + 5, currentY + 8);
        doc.text(String(type.name || 'N/A').substring(0, 35), margin + colWidths[0] + 5, currentY + 8);
        
        // Draw color swatch
        const color = type.color;
        if (color && color !== 'No color') {
          try {
            // Parse hex color to RGB
            let r, g, b;
            if (color.startsWith('#')) {
              const hex = color.substring(1);
              r = parseInt(hex.substring(0, 2), 16);
              g = parseInt(hex.substring(2, 4), 16);
              b = parseInt(hex.substring(4, 6), 16);
            } else {
              // Handle named colors
              const colorMap = {
                'red': [220, 53, 69],
                'blue': [0, 123, 255],
                'green': [40, 167, 69],
                'yellow': [255, 193, 7],
                'orange': [253, 126, 20],
                'purple': [111, 66, 193],
                'pink': [232, 62, 140],
                'cyan': [23, 162, 184]
              };
              const rgb = colorMap[color.toLowerCase()] || [128, 128, 128];
              [r, g, b] = rgb;
            }
            
            // Draw color square
            const colorX = margin + colWidths[0] + colWidths[1] + 10;
            const colorY = currentY + 2;
            
            doc.setFillColor(r, g, b);
            doc.rect(colorX, colorY, 10, 8, 'F');
            
            // Add border to the square
            doc.setDrawColor(100, 100, 100);
            doc.rect(colorX, colorY, 10, 8, 'S');
            
            // Add color code text
            doc.text(color.substring(0, 7), colorX + 12, currentY + 8);
          } catch (colorError) {
            // If color parsing fails, just show the color text
            doc.text(String(color || 'N/A').substring(0, 10), margin + colWidths[0] + colWidths[1] + 5, currentY + 8);
          }
        } else {
          doc.text('No color', margin + colWidths[0] + colWidths[1] + 5, currentY + 8);
        }
        
        currentY += rowHeight;
      });
      
      // Add page number to first page
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${pageNumber}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
      
      // Save the PDF
      doc.save('inspection-types.pdf');
      
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  // Pagination functions
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToFirstPage = () => {
    goToPage(1);
  };

  const goToPreviousPage = () => {
    goToPage(currentPage - 1);
  };

  const goToNextPage = () => {
    goToPage(currentPage + 1);
  };

  const goToLastPage = () => {
    goToPage(totalPages);
  };

  const getPageNumbers = () => {
    const pages = [];
    
    // For simplicity, show all pages if total is small
    // In a real implementation, you might want to show ellipsis for many pages
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const handleColumnVisibility = () => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  const toggleColumnVisibility = (columnKey) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Click outside handler for column visibility dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles.columnVisibilityDropdown}`) && 
          !event.target.closest(`.${styles.visibilityBtn}`)) {
        setShowColumnVisibility(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading inspection types...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Debug Toggle */}
      <div className={styles.debugToggle}>
        <label>
          <input 
            type="checkbox" 
            checked={useMockData} 
            onChange={toggleMockData}
          />
          Use Mock Data
        </label>
      </div>

      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <span 
          className={styles.breadcrumbLink}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span 
          className={styles.breadcrumbLink}
          onClick={() => navigate('/properties')}
        >
          Properties
        </span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.currentPage}>Inspection Types</span>
      </div>

      {/* Page Title */}
      <div className={styles.pageTitle}>
        <h2>Inspection Types</h2>
      </div>

      {/* Main Panel */}
      <div className={styles.panel}>
        <div className={styles.panelTitle}>
          <div className={styles.panelTitleLeft}>
            <h3>Inspection Types</h3>
          </div>
          <div className={styles.panelTitleRight}>
            <button 
              className={styles.btnPrimary}
              onClick={handleAddInspectionType}
            >
              <i className="glyphicon glyphicon-plus"></i> Add Inspection Type
            </button>
            <button 
              className={styles.btnSecondary}
              onClick={loadInspectionTypes}
              style={{ marginLeft: '10px' }}
            >
              <i className="glyphicon glyphicon-refresh"></i> Refresh
            </button>
          </div>
        </div>
        <div className={styles.panelBody}>
          {filteredInspectionTypes.length > 0 ? (
            <div>
              {/* DataTables Control Bar */}
              <div className={styles.dataTableControls}>
                <div className={styles.entriesControl}>
                  <label>Show entries:</label>
                  <select 
                    className={styles.entriesSelect}
                    value={entriesPerPage}
                    onChange={(e) => {
                      const newValue = Number(e.target.value);
                      setEntriesPerPage(newValue);
                      setCurrentPage(1); // Reset to first page when changing entries per page
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                    <option value={75}>75</option>
                    <option value={100}>100</option>
                  </select>
                </div>

                <div className={styles.searchControl}>
                  <label>Search:</label>
                  <input
                    ref={searchInputRef}
                    type="text"
                    className={styles.searchInput}
                    placeholder="Search inspection types..."
                    value={tableSearch}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTableSearch(value);
                      // Reset to first page when searching (handled in useEffect)
                    }}
                    onKeyDown={(e) => {
                      // Prevent form submission on Enter
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                  />
                  {isFiltering && (
                    <span className={styles.searchLoading} style={{ marginLeft: '10px', color: '#666' }}>
                      Searching...
                    </span>
                  )}
                </div>

                <div className={styles.actionButtons}>
                  <button 
                    className={styles.exportBtn}
                    onClick={handleExportCSV}
                  >
                    Export as CSV
                  </button>
                  <button 
                    className={styles.exportBtn}
                    onClick={handleExportPDF}
                  >
                    Export as PDF
                  </button>
                  <div className={styles.visibilityBtnContainer}>
                    <button 
                      className={styles.visibilityBtn}
                      onClick={handleColumnVisibility}
                    >
                      Column visibility
                    </button>
                    {showColumnVisibility && (
                      <div className={styles.columnVisibilityDropdown}>
                        <h4>Toggle Columns:</h4>
                        <label className={styles.columnToggle}>
                          <input
                            type="checkbox"
                            checked={columnVisibility.name}
                            onChange={() => toggleColumnVisibility('name')}
                          />
                          Name
                        </label>
                        <label className={styles.columnToggle}>
                          <input
                            type="checkbox"
                            checked={columnVisibility.color}
                            onChange={() => toggleColumnVisibility('color')}
                          />
                          Color
                        </label>
                        <label className={styles.columnToggle}>
                          <input
                            type="checkbox"
                            checked={columnVisibility.actions}
                            onChange={() => toggleColumnVisibility('actions')}
                          />
                          Actions
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.resultCount}>
                (Showing {filteredInspectionTypes.length} of {totalRecords} results)
              </div>

              {/* Data Table */}
              <div className={styles.tableWrapper}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      {columnVisibility.name && <th>Name</th>}
                      {columnVisibility.color && <th>Color</th>}
                      {columnVisibility.actions && <th className={styles.actionHead}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInspectionTypes.map((type) => (
                      <tr key={type.id}>
                        {columnVisibility.name && <td>{type.name}</td>}
                                                 {columnVisibility.color && (
                           <td>
                             {type.color ? (
                               <span 
                                 className={styles.colorLabel} 
                                 style={{ backgroundColor: type.color }}
                               >
                                 {type.color}
                               </span>
                             ) : (
                               <span className={styles.emptyColor}></span>
                             )}
                           </td>
                         )}
                        {columnVisibility.actions && (
                          <td className={styles.actionColumn}>
                            <button 
                              className={styles.btnEdit}
                              onClick={() => handleEditInspectionType(type.id)}
                              title="Edit inspection type"
                            >
                              Edit
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className={styles.pagination}>
                  <div className={styles.paginationInfo}>
                    Showing {((currentPage - 1) * entriesPerPage) + 1} to {Math.min(currentPage * entriesPerPage, totalRecords)} of {totalRecords} entries
                  </div>
                  <div className={styles.paginationControls}>
                    <button 
                      className={styles.paginationBtn}
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    
                    {getPageNumbers().map(page => (
                      <button
                        key={page}
                        className={`${styles.paginationBtn} ${page === currentPage ? styles.activePage : ''}`}
                        onClick={() => goToPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                    
                    <button 
                      className={styles.paginationBtn}
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.noResults}>
              {isFiltering ? (
                <div>
                  <div className="spinner-border spinner-border-sm" role="status" style={{ marginRight: '10px' }}>
                    <span className="sr-only">Loading...</span>
                  </div>
                  Searching...
                </div>
              ) : tableSearch ? (
                <div>
                  No inspection types found matching "{tableSearch}"
                  <button 
                    className={styles.clearSearchBtn}
                    onClick={() => {
                      setTableSearch('');
                      setCurrentPage(1);
                      if (searchInputRef.current) {
                        searchInputRef.current.focus();
                      }
                    }}
                    style={{ marginLeft: '10px', padding: '5px 10px', cursor: 'pointer' }}
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                <div>No inspection types!</div>
              )}
            </div>
          )}
        </div>
      </div>

      {useMockData && (
        <div className={styles.demoMode}>
          <strong>Demo Mode:</strong> This is running with mock data. Toggle the checkbox in the top-right corner to test with real API.
        </div>
      )}
    </div>
  );
};

export default InspectionTypes; 